<!-- DashboardReferee.vue -->
<template>
  <div class="cl-page">
    <div class="cl-pageheader">
      <div class="cl-pageheader-title">REFEREE Report Management</div>
      <button class="button_addreport" @click="addMatchReport">Add Report</button>
    </div>

    <div class="RL_note">
      List of loaded match reports. Click on "Add Report" to add a new report. Select a row to update or delete.
    </div>

    <div class="gridContainer">
      <div class="gridHeaders">
        <div class="gridItem gamedate">Date</div>
        <div class="gridItem gamenumber">Game Number</div>
        <div class="gridItem leaguetypeseason">LeagueTypeSeason</div>
        <div class="gridItem genderage">GenderAge</div>
        <div class="gridItem teams">Teams: Home vs. Away</div>
        <div class="gridItem yellowcard">YC</div>
        <div class="gridItem redcard">RC</div>
        <div class="gridItem warncard">WARN</div>
        <div class="gridItem removalcard">REMOVAL</div>
        <div class="gridItem actions">Actions</div>
      </div>

      <div class="gridContent">
        <ul>
          <li
            v-for="mreport in matchreports"
            :key="mreport.id"
            class="gridRow"
            @click="editMatchReport(mreport.id)"
          >
            <div class="gridItem gamedate">{{ mreport.GameDate }}</div>
            <div class="gridItem gamenumber">G{{ mreport.GameNumber }}</div>
            <div class="gridItem leaguetypeseason">{{ mreport.LeagueTypeSeason }}</div>
            <div class="gridItem genderage">{{ mreport.GenderAge }}</div>
            <div class="gridItem teams">{{ mreport.TeamHome }} vs. {{ mreport.TeamAway }}</div>
            <div class="gridItem yellowcard">
              <div v-if="mreport.YellowCardCount > 0" class="card-count yellow">{{ mreport.YellowCardCount }}</div>
            </div>
            <div class="gridItem redcard">
              <div v-if="mreport.RedCardCount > 0" class="card-count red">{{ mreport.RedCardCount }}</div>
            </div>
            <div class="gridItem warncard">
              <div v-if="mreport.WarnCount > 0" class="card-count warn">{{ mreport.WarnCount }}</div>
            </div>
            <div class="gridItem removalcard">
              <div v-if="mreport.RemovalCount > 0" class="card-count removal">{{ mreport.RemovalCount }}</div>
            </div>
            <div class="gridItem actions" @click.stop>
              <span class="material-icons edit" @click="editMatchReport(mreport.id)">edit</span>
              <span class="material-icons delete" @click="deleteMatchReport(mreport.id)">delete</span>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { db, matchreportsColRef, misconductColRef } from "@/firebase";
import { query, where, orderBy, getDocs, deleteDoc, doc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";

const router = useRouter();
const matchreports = ref([]);

// Header fields pulled from localStorage (kept in sync)
const org = ref(localStorage.getItem("currentUserOrgName") || "");
const role = ref(localStorage.getItem("currentUserRole") || "");
const nameFirst = ref(localStorage.getItem("currentUserNameFirst") || "");
const nameLast = ref(localStorage.getItem("currentUserNameLast") || "");

function syncFromStorage() {
  org.value = localStorage.getItem("currentUserOrgName") || org.value;
  role.value = localStorage.getItem("currentUserRole") || role.value;
  nameFirst.value = localStorage.getItem("currentUserNameFirst") || nameFirst.value;
  nameLast.value = localStorage.getItem("currentUserNameLast") || nameLast.value;
}

const navigateToLogin = () => router.push("/login");

onMounted(async () => {
  window.addEventListener("storage", syncFromStorage);

  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    navigateToLogin();
    return;
  }

  // ✅ FIX 1: Validate token and claims before loading data
  try {
    await validateUserAccess(user);
  } catch (error) {
    console.error("Access validation failed:", error);
    alert(error.message);
    navigateToLogin();
    return;
  }

  // Load basic profile from users/{uid} only
  await hydrateFromUsersDoc(user.uid);

  // If users doc is missing first/last, trigger profile dialog from AppTopBar
  if (!nameFirst.value?.trim() || !nameLast.value?.trim()) {
    window.dispatchEvent(
      new CustomEvent("open-profile", {
        detail: { note: "Please complete your profile information to get started." },
      })
    );
  }

  if (org.value) {
    await loadMatchReports(user.uid, org.value);
  }
});

onUnmounted(() => {
  window.removeEventListener("storage", syncFromStorage);
});

/**
 * ✅ NEW: Validate user has proper access to current organization
 */
async function validateUserAccess(user) {
  // Force refresh token to get latest claims
  await user.getIdToken(true);
  const idTokenResult = await user.getIdTokenResult(true);
  const claims = idTokenResult.claims;

  console.log("Dashboard - Validating claims:", claims);

  // Check if claims exist
  if (!claims || !claims.orgRoles) {
    throw new Error("No access roles found. Please contact administrator.");
  }

  // Get stored org and role from localStorage
  const storedOrg = localStorage.getItem("currentUserOrgName");
  const storedRole = localStorage.getItem("currentUserRole");
  console.log(`📖 DashboardReferee reading localStorage.currentUserRole: ${storedRole}`);

  if (!storedOrg || !storedRole) {
    throw new Error("Session data missing. Please log in again.");
  }

  // Validate user has access to the stored organization
  const rolesMap = claims.orgRoles || {}; // Use orgRoles from new claims structure
  const claimsRolesForOrg = rolesMap[storedOrg];

  if (!claimsRolesForOrg || claimsRolesForOrg.length === 0) {
    const availableOrgs = Object.keys(rolesMap).join(", ") || "(none)";
    throw new Error(
      `You no longer have access to "${storedOrg}". Available organizations: ${availableOrgs}`
    );
  }

  // For multi-role users, trust the stored role selection
  // The ActiveRole in Firestore should have been updated during login
  console.log(`✅ User selected role: ${storedRole}, Available roles in org: ${claimsRolesForOrg.join(", ")}`);
  
  // Set the role to user's selection
  role.value = storedRole;
  
  // Note: If claims don't match, it's okay - they will be updated on next login
  if (!claimsRolesForOrg.includes(storedRole)) {
    console.log(`ℹ️ Claims will be updated to match selected role on next token refresh`);
  }

  // Validate active claims match localStorage
  if (claims.organization !== storedOrg || claims.role !== storedRole) {
    console.log(`ℹ️ Claims (role: ${claims.role}) don't match localStorage (role: ${storedRole})`);
    console.log(`ℹ️ This is expected with orgRoles structure - using localStorage selection`);
    // Optionally sync or force re-login
  }

  // ✅ Check token expiration
  const expirationTime = new Date(idTokenResult.expirationTime);
  const now = new Date();
  const timeUntilExpiry = expirationTime - now;

  console.log(`Token expires in ${Math.floor(timeUntilExpiry / 1000 / 60)} minutes`);

  // If token expires in less than 5 minutes, refresh it
  if (timeUntilExpiry < 5 * 60 * 1000) {
    console.log("Token expiring soon, refreshing...");
    await user.getIdToken(true);
  }

  return true;
}

async function hydrateFromUsersDoc(uid) {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (!snap.exists()) return;

    const u = snap.data() || {};
    const first = (u.NameFirst || "").trim();
    const last = (u.NameLast || "").trim();
    // 🔧 FIX: Don't overwrite org from localStorage
       const storedOrg = localStorage.getItem("currentUserOrgName");
       const storedRole = localStorage.getItem("currentUserRole");

    // 🔧 FIX: Always use localStorage values, no condition needed
       {
      // 🔧 FIX: Use localStorage value, not first role in array
         if (storedOrg) {
           org.value = storedOrg;
         } else if (u.ActiveOrg) {
           org.value = u.ActiveOrg;
         }
         
         // Set the role from what user selected during login
         if (storedRole) {
           role.value = storedRole;
         }
      localStorage.setItem("currentUserOrgName", storedOrg || u.ActiveOrg || "");
    }
    if (first) {
      nameFirst.value = first;
      localStorage.setItem("currentUserNameFirst", first);
    }
    if (last) {
      nameLast.value = last;
      localStorage.setItem("currentUserNameLast", last);
    }
  } catch (e) {
    console.error("hydrateFromUsersDoc error:", e);
  }
}

async function loadMatchReports(userId, userOrg) {
  try {
    const qMR = query(
      matchreportsColRef,
      where("UserID", "==", userId),
      where("Organization", "==", userOrg),
      orderBy("GameDate", "desc")
    );

    const qMRSnapshot = await getDocs(qMR);
    matchreports.value = qMRSnapshot.docs.map((d) => {
      const data = d.data();
      const sourceDate =
        data.GameDate?.toDate?.() ??
        (typeof data.GameDate === "string" || typeof data.GameDate === "number"
          ? new Date(data.GameDate)
          : null);

      return {
        id: d.id,
        ...data,
        GameDate: sourceDate ? new Date(sourceDate).toLocaleDateString() : "N/A",
      };
    });
  } catch (error) {
    console.error("Error loading match reports:", error);
  }
}

function addMatchReport() {
  router.push({
    name: "MatchReport",
    params: { id: "NEW" },
    query: { isNew: true },
  });
}

function editMatchReport(id) {
  router.push({
    name: "MatchReport",
    params: { id },
    query: { isNew: false },
  });
}

async function deleteMatchReport(id) {
  if (!confirm("Are you sure you want to delete this match report?")) return;

  try {
    // Delete child misconduct docs first
    const misQ = query(misconductColRef, where("MatchReportID", "==", id));
    const misSnap = await getDocs(misQ);
    await Promise.all(misSnap.docs.map((d) => deleteDoc(d.ref)));

    // Delete the match report
    await deleteDoc(doc(matchreportsColRef, id));

    // Update local list
    matchreports.value = matchreports.value.filter((r) => r.id !== id);
  } catch (error) {
    console.error("Error deleting match report and misconduct reports:", error);
  }
}
</script>

<style scoped>
/* Page - Responsive */
.cl-page {
  min-height: 100vh;
  background: radial-gradient(1200px 600px at 20% 0%, #eac891 0%, #eac891 40%, #f2d9ad 100%);
  padding-top: 80px; /* Account for any top navigation */
}

/* Header - Responsive */
.cl-pageheader {
  position: sticky;
  top: 0;
  height: 60px;
  width: 100%;
  padding: 10px 20px;
  border-top: 5px solid #02b8cc;
  border-bottom: 1px solid #32e3f7;
  background-color: #e78d58;
  color: #000;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;
  box-sizing: border-box;
}

.cl-pageheader-title {
  color: #000;
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  font-weight: bold;
  flex: 1;
}

.button_addreport {
  height: 40px;
  min-width: 120px;
  padding: 8px 20px;
  border-radius: 12px;
  background-color: #99cc00;
  box-shadow: 0 4px 8px rgba(0,0,0,.2), 0 6px 20px rgba(0,0,0,.19);
  font-size: clamp(0.9rem, 2vw, 1rem);
  color: #000099;
  border: none;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.button_addreport:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0,0,0,.3), 0 8px 25px rgba(0,0,0,.25);
}

/* Note under header - Responsive */
.RL_note {
  padding: 20px;
  margin: 0 20px;
  font-size: clamp(0.9rem, 2vw, 1rem);
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Report grid - Responsive */
.gridContainer {
  margin: 20px 40px 20px 40px !important;
  padding: 0 !important;
  overflow-x: auto; /* Allow horizontal scrolling on small screens */
  font-size: 14px;
  max-height: calc(100vh - 240px); /* Dynamic height based on viewport */
  overflow-y: auto;
  box-sizing: border-box !important;
  width: calc(100% - 80px) !important;
}

.gridHeaders {
  display: grid;
  grid-template-columns: 
    120px /* Date */
    150px /* Game Number */
    minmax(250px, 1fr) /* League Type Season */
    120px /* Gender Age */
    minmax(400px, 2fr) /* Teams */
    40px  /* YC */
    40px  /* RC */
    60px  /* WARN */
    80px  /* REMOVAL */
    90px; /* Actions */
  gap: 8px;
  padding: 8px;
  background-color: #e9ecef;
  margin-bottom: 4px;
  min-width: 1200px; /* Minimum width to prevent squishing */
}

.gridContent ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.gridRow {
  display: grid;
  grid-template-columns: 
    120px /* Date */
    150px /* Game Number */
    minmax(250px, 1fr) /* League Type Season */
    120px /* Gender Age */
    minmax(400px, 2fr) /* Teams */
    40px  /* YC */
    40px  /* RC */
    60px  /* WARN */
    80px  /* REMOVAL */
    90px; /* Actions */
  gap: 8px;
  margin-bottom: 4px;
  border-radius: 10px;
  background: #fff;
  font-size: 14px;
  min-height: 40px;
  align-items: center;
  min-width: 100px; /* Match header minimum width */
}

.gridItem { 
  padding: clamp(5px, 1vw, 8px);
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.gridItem.gamedate { justify-self: start; }
.gridItem.gamenumber { justify-self: center; }
.gridItem.leaguetypeseason { justify-self: left; }
.gridItem.genderage { justify-self: center; }
.gridItem.teams { justify-self: start; }
.gridItem.yellowcard { justify-self: center; }
.gridItem.redcard { justify-self: center; }
.gridItem.warncard { justify-self: center; }
.gridItem.removalcard { justify-self: center; }
.gridItem.actions { justify-self: center; }

/* Row hover - Responsive */
.gridContainer li:hover {
  cursor: pointer;
  background: #e6f5ff;
  transition: background-color 0.2s;
}

/* Action icons - Responsive */
.material-icons {
  cursor: pointer;
  font-size: clamp(16px, 3vw, 18px);
  color: #888;
  margin: 0 2px;
  transition: color 0.2s, transform 0.2s;
}

.material-icons:hover {
  transform: scale(1.1);
}

.material-icons.edit:hover { color: #005ce6; }
.material-icons.delete:hover { color: red; }

/* Card counts - Responsive */
.card-count {
  display: flex;
  justify-content: center;
  align-items: center;
  width: clamp(20px, 3vw, 25px);
  height: clamp(20px, 3vw, 25px);
  border-radius: 35%;
  border: 1px solid black;
  font-size: clamp(12px, 2vw, 14px);
  font-weight: bold;
}

.card-count.yellow { background-color: yellow; color: black; }
.card-count.red    { background-color: red;    color: white; }
.card-count.warn   { background-color: orange; color: black; }
.card-count.removal { background-color: darkred; color: white; }

/* Tablet responsive adjustments */
@media (max-width: 1024px) {
  .gridHeaders {
    grid-template-columns: 
      minmax(80px, 100px) /* Date */
      minmax(100px, 120px) /* Game Number */
      minmax(150px, 200px) /* League Type Season */
      minmax(80px, 100px) /* Gender Age */
      minmax(200px, 350px) /* Teams */
      minmax(25px, 40px)  /* YC */
      minmax(25px, 40px)  /* RC */
      minmax(40px, 55px)  /* WARN */
      minmax(55px, 70px)  /* REMOVAL */
      minmax(60px, 80px); /* Actions */
    min-width: 900px;
  }
  
  .gridRow {
    grid-template-columns: 
      minmax(80px, 100px) /* Date */
      minmax(100px, 120px) /* Game Number */
      minmax(150px, 200px) /* League Type Season */
      minmax(80px, 100px) /* Gender Age */
      minmax(200px, 350px) /* Teams */
      minmax(25px, 40px)  /* YC */
      minmax(25px, 40px)  /* RC */
      minmax(40px, 55px)  /* WARN */
      minmax(55px, 70px)  /* REMOVAL */
      minmax(60px, 80px); /* Actions */
    min-width: 900px;
  }
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .cl-pageheader {
    flex-direction: column;
    height: auto;
    padding: 15px;
    gap: 10px;
  }
  
  .cl-pageheader-title {
    text-align: center;
    margin-bottom: 5px;
  }
  
  .button_addreport {
    width: 100%;
    max-width: 200px;
  }
  
  .RL_note {
    margin: 10px;
    padding: 15px;
    font-size: 0.9rem;
  }
  
  .gridContainer {
    margin: 10px;
    max-height: calc(100vh - 300px);
  }
  
  .gridHeaders {
    grid-template-columns: 
      minmax(60px, 80px) /* Date */
      minmax(80px, 100px) /* Game Number */
      minmax(120px, 150px) /* League Type Season */
      minmax(60px, 80px) /* Gender Age */
      minmax(150px, 200px) /* Teams */
      minmax(25px, 35px)  /* YC */
      minmax(25px, 35px)  /* RC */
      minmax(30px, 45px)  /* WARN */
      minmax(45px, 60px)  /* REMOVAL */
      minmax(50px, 60px); /* Actions */
    min-width: 750px;
    gap: 3px;
    padding: 5px;
    font-size: 11px;
  }
  
  .gridRow {
    grid-template-columns: 
      minmax(60px, 80px) /* Date */
      minmax(80px, 100px) /* Game Number */
      minmax(120px, 150px) /* League Type Season */
      minmax(60px, 80px) /* Gender Age */
      minmax(150px, 200px) /* Teams */
      minmax(25px, 35px)  /* YC */
      minmax(25px, 35px)  /* RC */
      minmax(30px, 45px)  /* WARN */
      minmax(45px, 60px)  /* REMOVAL */
      minmax(50px, 60px); /* Actions */
    min-width: 750px;
    gap: 3px;
    font-size: 11px;
    min-height: 35px;
  }
  
  .gridItem {
    padding: 3px;
  }
  
  .card-count {
    width: 18px;
    height: 18px;
    font-size: 10px;
  }
  
  .material-icons {
    font-size: 14px;
    margin: 0 1px;
  }
}

/* Very small screens */
@media (max-width: 480px) {
  .gridHeaders {
    min-width: 550px;
    font-size: 10px;
  }
  
  .gridRow {
    min-width: 550px;
    font-size: 10px;
  }
  
  .RL_note {
    font-size: 0.8rem;
  }
}

/* High DPI screens */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .material-icons {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
}

/* Print styles */
@media print {
  .cl-page {
    background: white;
  }
  
  .cl-pageheader {
    background: white;
    border: 1px solid black;
    color: black;
    position: static;
  }
  
  .button_addreport {
    display: none;
  }
  
  .gridContainer {
    overflow: visible;
    max-height: none;
  }
  
  .gridRow:hover {
    background: white;
  }
}
</style>